<template>
    <div>
        <h1 class="display-heading">Invite Company Users</h1>

        <p>Invite users to join {{ companyName }} Platform!</p>

        <div class="card mb-3">
            <div class="card-header">Invitation Report</div>
            <div class="card-body">
                <p>
                    View previously invited user list and their sign-up statuses.
                </p>

                <router-link :to="{'name': 'company-user-invitation-report'}" class="btn btn-primary btn-primary-insv">
                    View Invitation Report
                </router-link>
            </div>
        </div>

        <div class="card mb-3">
            <div class="card-header">Bulk Invite Company Users</div>
            <div class="card-body">
                <p>Bulk invite multiple users to join the platform.</p>

                <router-link :to="{'name': 'company-bulk-invite-users'}" class="btn btn-primary btn-primary-insv">
                    Bulk Invite Company Users
                </router-link>
            </div>
        </div>

        <div class="card">
            <div class="card-header">Manual Invite Company Users</div>
            <div class="card-body">
                <div class="mb-3">
                    <h5>Instructions</h5>
                    <ol>
                        <li>Enter employee's e-mail address in the text field below.</li>
                        <li>You may click the "Add More" button to add more employee details.</li>
                        <li>On completion, click "Invite Now" button to proceed with your invitation.</li>
                    </ol>
                </div>

                <form @submit.prevent="validateEmails">
                    <table ref="tableUserInvite" class="table mb-3 table-form">
                        <colgroup>
                            <col width="50">
                            <col>
                            <col>
                            <col width="120">
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="col">#</th>
                                <th scope="col" class="text-nowrap">Name</th>
                                <th scope="col" class="text-nowrap">E-mail Address</th>
                                <th scope="col">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(invite, index) in inviteList" :key="index">
                                <th scope="row">{{index + 1}}</th>
                                <td>
                                    <input v-model="inviteList[index].name" type="text" placeholder="Name"
                                        :name="`name${index}`" data-vv-validate-on="blur" data-vv-as="name"
                                        v-validate="'required'" :class="{
                                        'form-control': true,
                                        'is-invalid':  validationErrors.has(`name${index}`)
                                    }">
                                    <div v-show="validationErrors.has(`name${index}`)" class="invalid-feedback">
                                        {{ validationErrors.first(`name${index}`) }}
                                    </div>
                                </td>
                                <td>
                                    <input v-model="inviteList[index].email" type="text" placeholder="E-mail Address"
                                        :name="`email${index}`" data-vv-validate-on="blur" data-vv-as="email address"
                                        v-validate="'required|email'" :class="{
                                            'form-control': true,
                                            'is-invalid':  validationErrors.has(`email${index}`)
                                        }">
                                    <div v-show="validationErrors.has(`email${index}`)" class="invalid-feedback">
                                        {{ validationErrors.first(`email${index}`) }}
                                    </div>
                                </td>
                                <td>
                                    <button type="button" class="btn btn-danger"
                                        @click="deleteEmailAddress(index)">Delete</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <button type="button" class="btn btn-primary btn-primary-insv mr-2 mb-2" @click="addInviteField">
                        Add More
                    </button>
                    <button type="button" class="btn btn-primary btn-primary-insv mr-2 mb-2" @click="inviteUsers">
                        Invite Users Now</button>
                </form>
            </div>
        </div>

        <popup-modal ref="popupAddDone" title="Invite Users" sizeClass="modal-lg">
            <template slot="content">
                <p>{{inviteResultText}}</p>

                <template v-if="inviteWithErrors">
                    <p>{{inviteResultErrorText}}</p>

                    <table class="table">
                        <colgroup>
                            <col width="50">
                            <col>
                            <col>
                            <col>
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="col">#</th>
                                <th scope="col">Name</th>
                                <th scope="col">Email Address</th>
                                <th scope="col">Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(invite, index) in inviteErrorList" :key="index">
                                <th scope="row">{{index+1}}</th>
                                <td>{{invite.name ? invite.name : '-'}}</td>
                                <td>{{invite.email}}</td>
                                <td>{{invite.reason ? invite.reason : '-'}}</td>
                            </tr>
                        </tbody>
                    </table>
                </template>
            </template>
            <template slot="footer">
                <button type="button" class="btn btn-primary btn-primary-insv"
                    @click="dismissDoneConfirmation">Okay</button>
            </template>
        </popup-modal>
    </div>
</template>

<script>
import popupModal from '@/components/popup-modal';
export default {
    'name': 'CompanyUserInvitation',
    'components': {
        popupModal
    },
    'computed': {
        companyName () {
            return this.$store.state.operator.companyNameDisplay;
        }
    },
    data () {
        return {
            'inviteList': [{
                'name': '',
                'email': ''
            }],
            'inviteWithErrors': false,
            'inviteResultText': '',
            'inviteResultErrorText': '',
            'inviteErrorList': []
        };
    },
    mounted () {},
    'methods': {
        addInviteField () {
            this.inviteList.push({
                'name': '',
                'email': ''
            });

            this.$nextTick(() => {
                let lastInsertedRow = this.$refs.tableUserInvite.querySelectorAll('tr')[this.inviteList.length];
                lastInsertedRow.querySelector('input').focus();
            });
        },
        deleteEmailAddress (index) {
            this.inviteList.splice(index, 1);

            if (this.inviteList.length === 0) {
                this.inviteList.push({
                    'name': '',
                    'email': ''
                });

                this.$validator.reset();
            }
        },
        validateEmails () {
            return this.$validator.validateAll();
        },
        inviteUsers () {
            this.validateEmails().then((validated) => {
                if (validated) {
                    let emails = [];

                    emails = this.inviteList.map((invite) => ({
                        'e': invite.email,
                        'n': invite.name
                    }));

                    this.axios.post('/Invite/Company', emails).then(({
                        data
                    }) => {
                        let successList = data.data.si;
                        let failedList = data.data.fi;

                        this.inviteResultText =
                                `${successList.length} invitation${successList.length > 1 ? 's' : ''} created.`;

                        if (failedList.length > 0) {
                            this.inviteWithErrors = true;
                            this.inviteResultErrorText =
                                    `${failedList.length} failed invitation${failedList.length > 1 ? 's' : ''}.`;
                            this.inviteErrorList = failedList.map((invitation) => ({
                                'email': invitation.e,
                                'name': invitation.n,
                                'reason': invitation.fr
                            }));
                        } else {
                            this.inviteWithErrors = false;
                            this.inviteResultErrorText = '';
                            this.inviteErrorList = [];
                        }

                        this.inviteList = [];
                        this.inviteList.push({
                            'name': '',
                            'email': ''
                        });

                        this.$refs.popupAddDone.show();
                    }).catch(() => {
                        this.inviteResultText = 'Error saving invitations. Please try again.';
                        this.$refs.popupAddDone.show();
                    });
                }
            });
        },
        dismissDoneConfirmation () {
            this.$refs.popupAddDone.hide();
        }
    }
};

</script>

<style>

</style>
